import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import CachedIcon from '@mui/icons-material/Cached';
import SaveIcon from '@mui/icons-material/Save';
import { Button, SmallLoader } from 'framework/ui/mui-builder';
import { useFieldHelper } from 'framework/ui/field';
import { useDocumentStorageContext } from 'framework/documentStorage';
import { useFormContext, useFormShowContext } from 'framework/form';
import useDocumentBlob from '../useDocumentBlob';
import DocumentPreviewRenderer from './DocumentPreviewRenderer';
import { getRenderPayloadFromFormData } from './templatePreviewPayloadHelper';
import { useSearchParams } from 'react-router-dom';

const DOCUMENT_TEMP_SOURCE = 'documentTempPath';

type DocumentTemplatePreviewSectionProps = {
  actions?: React.ReactNode;
  isShowActions?: boolean;
};

const DocumentTemplatingWithPreview = ({ actions, isShowActions = true }: DocumentTemplatePreviewSectionProps) => {
  const [pendingSubmit, setPendingSubmit] = useState(false);
  const [reloadLoading, setReloadLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [searchParams] = useSearchParams();

  const { getValue } = useFieldHelper<Nullable<string>>({ source: 'documentPath' });
  const { getValue: getSourceTemplatePath } = useFieldHelper<Nullable<string>>({ source: 'sourceTemplatePath' });
  const documentPath = getValue();
  const sourceTemplatePath = getSourceTemplatePath();
  const sourcePreviewPath = sourceTemplatePath || documentPath;
  const { data, isSubmitEnabled, submitForm, onChangeCallback } = useFormContext();
  const { isEditMode } = useFormShowContext();
  const { renderDocumentTemplate, renderPreviewDocumentTemplate } = useDocumentStorageContext();

  const { blobUrl, loading, loadBlob, hasError } = useDocumentBlob(documentPath);

  const handleReload = async () => {
    if (!sourcePreviewPath) return;
    setReloadLoading(true);

    try {
      if (!renderPreviewDocumentTemplate) return;
      const renderPayload = getRenderPayloadFromFormData((data?.form as Record<string, unknown>) ?? undefined);

      const requestReload = await renderPreviewDocumentTemplate({
        templatePath: sourcePreviewPath,
        highlight: false,
        ...renderPayload,
      });
      await loadBlob(requestReload.path);
      onChangeCallback?.({ [DOCUMENT_TEMP_SOURCE]: requestReload.path }, true);
    } finally {
      setReloadLoading(false);
    }
  };

  const handleSave = async () => {
    if (!sourcePreviewPath) return;
    setSaveLoading(true);
    try {
      const compensationEntity = searchParams.get('CompensationEntity');
      const entityIdentifiers = compensationEntity ? { CompensationEntity: compensationEntity } : undefined;
      const renderPayload = getRenderPayloadFromFormData((data?.form as Record<string, unknown>) ?? undefined);
      const requestSave = await renderDocumentTemplate!({
        templatePath: sourcePreviewPath,
        entityIdentifiers,
        ...renderPayload,
      });
      await loadBlob(requestSave.path);
      onChangeCallback?.({ [DOCUMENT_TEMP_SOURCE]: requestSave.path }, true);
      setPendingSubmit(true);
    } finally {
      setSaveLoading(false);
    }
  };

  const handlePrint = () => {
    if (!blobUrl) return;
    const printWindow = window.open(blobUrl);
    if (!printWindow) return;
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
  };

  useEffect(() => {
    if (pendingSubmit) {
      submitForm?.();
      setPendingSubmit(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <DocumentPreviewRenderer documentPath={documentPath!} blobUrl={blobUrl!} loading={loading} hasError={hasError} />

      {isShowActions &&
        (isEditMode ? (
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Button color="primary" onClick={handleReload} disabled={reloadLoading || !isSubmitEnabled}>
              <Typography mr="4px">Reload</Typography>
              {reloadLoading ? <SmallLoader /> : <CachedIcon sx={{ fontSize: 16 }} />}
            </Button>

            <Box display="flex" gap={1} justifyContent="flex-end">
              <Button color="primary" onClick={handlePrint}>
                <Typography mr="4px">Print</Typography>
                <PrintIcon sx={{ fontSize: 16 }} />
              </Button>

              <Button color="primary" onClick={handleSave} disabled={saveLoading || !isSubmitEnabled}>
                <Typography mr="4px">Save</Typography>
                {saveLoading ? <SmallLoader /> : <SaveIcon sx={{ fontSize: 16 }} />}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box display="flex" gap={2} justifyContent="flex-end">
            <Button color="primary" onClick={handlePrint}>
              <Typography mr="4px">Print</Typography>
              <PrintIcon sx={{ fontSize: 16 }} />
            </Button>
            {actions}
          </Box>
        ))}
    </Box>
  );
};

export default DocumentTemplatingWithPreview;
